/**
 * ConnectorDialogShell — the ONE modal chrome for connector connect/edit flows.
 *
 * Four dialogs (ConnectAccountDialog, EditConnectorDialog,
 * SpreadsheetConnectDialog, FilesystemConnectDialog) hand-rolled the exact
 * same Radix chrome — overlay, centered card, sr-only Title, corner close
 * button — copy-pasted. This shell owns all of it; consumers render only their
 * header + body as ``children``.
 *
 * Pencil tweak (Jul 2026): the copied chrome carried ``backdrop-blur-sm`` on
 * the overlay — a glassmorphism smell the Ando pencil language bans. The shell
 * drops the blur (a slightly darker flat scrim reads cleaner and is cheaper).
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import type * as React from "react";

import { cn } from "@ando/ui";

export type ConnectorDialogSize = "sm" | "md" | "lg";

const SIZE_CLASS: Record<ConnectorDialogSize, string> = {
  sm: "w-[min(480px,92vw)]",
  md: "w-[min(520px,92vw)]",
  lg: "w-[min(640px,94vw)]",
};

export interface ConnectorDialogShellProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Accessible dialog title (sr-only Radix Title). Consumers still render
   *  their own visible heading inside ``children``. */
  readonly title: string;
  readonly size?: ConnectorDialogSize;
  /** Extra classes on the content card (rarely needed). */
  readonly className?: string;
  readonly children: React.ReactNode;
}

export const ConnectorDialogShell: React.FC<ConnectorDialogShellProps> = ({
  open,
  onOpenChange,
  title,
  size = "sm",
  className,
  children,
}) => {
  const { i18n } = useLingui();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            // No backdrop-blur — flat warm scrim (pencil).
            "fixed inset-0 z-50 bg-black/45",
            "data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in",
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50",
            "max-h-[90vh] overflow-y-auto",
            "rounded-xl border border-hairline bg-cream p-5 shadow-2xl",
            "data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in",
            SIZE_CLASS[size],
            className,
          )}
        >
          {/* Radix requires a Title inside Content; consumers render their own
              visible heading, so we mount an always-on sr-only one. */}
          <VisuallyHidden.Root>
            <DialogPrimitive.Title>{title}</DialogPrimitive.Title>
          </VisuallyHidden.Root>

          <DialogPrimitive.Close
            aria-label={i18n._(msg`Chiudi`)}
            className={cn(
              "absolute top-4 right-4 flex h-7 w-7 items-center justify-center rounded-md text-ink-3",
              "transition-colors hover:bg-black/[0.04] hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
            )}
          >
            <X className="h-4 w-4" aria-hidden />
          </DialogPrimitive.Close>

          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
ConnectorDialogShell.displayName = "ConnectorDialogShell";
